import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Button, Input, Select } from "@/shared/ui";
import { useCountries } from "@/hooks/useCountries";
import { updateClientProfile } from "@/shared/db";
import { isValidEmail } from "@/shared/core";
import { updateProfile } from "@/shared/db";
import { toast } from "sonner";
import { motion } from "framer-motion";
import type { ClientWithStats, ClientFormData } from "../types/client.types";

interface EditClientModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  client: ClientWithStats | null;
}

export const EditClientModal: React.FC<EditClientModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  client,
}) => {
  const { t, i18n } = useTranslation();
  const { countries } = useCountries();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ClientFormData>({
    full_name: "",
    email: "",
    country: "MX",
    phone: "",
    client_type: "individual",
    company_name: "",
    address: "",
    legal_help_type: "",
  });

  const [errors, setErrors] = useState<
    Partial<Record<keyof ClientFormData, string>>
  >({});

  useEffect(() => {
    if (client && client.profiles) {
      setFormData({
        full_name: client.profiles.full_name || "",
        email: client.profiles.email || "",
        country: client.profiles.country || "MX",
        phone: client.phone || "",
        client_type: client.client_type as
          | "individual"
          | "business"
          | "nonprofit",
        company_name: client.company_name || "",
        address: client.address || "",
        legal_help_type: client.legal_help_type || "",
      });
    }
  }, [client]);

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof ClientFormData, string>> = {};

    if (!formData.full_name.trim()) {
      newErrors.full_name = t("lawyer.editClient.errors.nameRequired");
    }

    if (!formData.email.trim()) {
      newErrors.email = t("lawyer.editClient.errors.emailRequired");
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = t("lawyer.editClient.errors.emailInvalid");
    }

    if (!formData.country) {
      newErrors.country = t("lawyer.editClient.errors.countryRequired");
    }

    if (formData.client_type === "business" && !formData.company_name?.trim()) {
      newErrors.company_name = t("lawyer.editClient.errors.companyRequired");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!client || !validateForm()) {
      return;
    }

    try {
      setLoading(true);

      // Update profile table (name, email, country)
      await updateProfile(client.id, {
        full_name: formData.full_name,
        email: formData.email,
        country: formData.country,
      });

      // Update client_profiles table
      await updateClientProfile(client.id, {
        phone: formData.phone || null,
        client_type: formData.client_type,
        company_name: formData.company_name || null,
        address: formData.address || null,
        legal_help_type: formData.legal_help_type || null,
      });

      toast.success(t("lawyer.editClient.success"));
      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Error updating client:", error);
      toast.error(t("lawyer.editClient.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setErrors({});
      onClose();
    }
  };

  if (!isOpen || !client) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Background overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full z-10 overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-linear-to-r from-tiber-700 to-tiber-500 px-8 py-6">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white">
                {t("lawyer.editClient.title")}
              </h3>
              <button
                onClick={handleClose}
                disabled={loading}
                className="text-white/80 hover:text-white cursor-pointer transition-colors p-2 hover:bg-white/10 rounded-xl disabled:opacity-50"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.editClient.fullName")} *
              </label>
              <Input
                type="text"
                value={formData.full_name}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, full_name: e.target.value })
                }
                placeholder={t(
                  "lawyer.editClient.fullNamePlaceholder",
                  "John Doe",
                )}
                disabled={loading}
                className={errors.full_name ? "border-red-500" : ""}
              />
              {errors.full_name && (
                <p className="mt-1 text-sm text-red-600">{errors.full_name}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.editClient.email")} *
              </label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                placeholder={t(
                  "lawyer.editClient.emailPlaceholder",
                  "john.doe@example.com",
                )}
                disabled={loading}
                className={errors.email ? "border-red-500" : ""}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Phone & Country */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-tiber-700 mb-2">
                  {t("lawyer.editClient.phone")}
                </label>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  placeholder="+1 234 567 8900"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-tiber-700 mb-2">
                  {t("lawyer.editClient.country")} *
                </label>
                <Select
                  value={formData.country}
                  onChange={(e) =>
                    setFormData({ ...formData, country: e.target.value })
                  }
                  disabled={loading}
                  options={[
                    ...countries.map((c) => ({
                      value: c.code,
                      label: i18n.language === "es" ? c.nameEs : c.nameEn,
                    })),
                  ]}
                />
                {errors.country && (
                  <p className="mt-1 text-sm text-red-600">{errors.country}</p>
                )}
              </div>
            </div>

            {/* Client Type */}
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.editClient.clientType")} *
              </label>
              <Select
                value={formData.client_type}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    client_type: e.target.value as
                      | "individual"
                      | "business"
                      | "nonprofit",
                  })
                }
                disabled={loading}
                options={[
                  {
                    value: "individual",
                    label: t(
                      "lawyer.editClient.types.individual",
                      "Individual",
                    ),
                  },
                  {
                    value: "business",
                    label: t("lawyer.editClient.types.business"),
                  },
                  {
                    value: "nonprofit",
                    label: t("lawyer.editClient.types.nonprofit"),
                  },
                ]}
              />
            </div>

            {/* Company Name (conditional) */}
            {formData.client_type === "business" && (
              <div>
                <label className="block text-sm font-medium text-tiber-700 mb-2">
                  {t("lawyer.editClient.companyName")} *
                </label>
                <Input
                  type="text"
                  value={formData.company_name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, company_name: e.target.value })
                  }
                  placeholder={t(
                    "lawyer.editClient.companyNamePlaceholder",
                    "ABC Corporation",
                  )}
                  disabled={loading}
                  className={errors.company_name ? "border-red-500" : ""}
                />
                {errors.company_name && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.company_name}
                  </p>
                )}
              </div>
            )}

            {/* Address */}
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.editClient.address")}
              </label>
              <Input
                type="text"
                value={formData.address}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, address: e.target.value })
                }
                placeholder={t(
                  "lawyer.editClient.addressPlaceholder",
                  "123 Main St, City, State",
                )}
                disabled={loading}
              />
            </div>

            {/* Legal Help Type */}
            <div>
              <label className="block text-sm font-medium text-tiber-700 mb-2">
                {t("lawyer.editClient.legalHelp")}
              </label>
              <Input
                type="text"
                value={formData.legal_help_type}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setFormData({ ...formData, legal_help_type: e.target.value })
                }
                placeholder={t(
                  "lawyer.editClient.legalHelpPlaceholder",
                  "Contract review, legal consultation, etc.",
                )}
                disabled={loading}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-6 border-t border-tiber-100">
              <Button
                type="button"
                variant="ghost"
                onClick={handleClose}
                disabled={loading}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? t("common.updating") : t("lawyer.editClient.update")}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};
